import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import {
  adminFixtureAccounts,
  adminFixtureClients,
  useAdminFixturesFromQuery,
} from '../fixtures'
import type { AdminTradingAccount } from '../types'
import { money } from '../../client/prediction-filter'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { formatWhen, maskLogin, safeAdminError } from '../safe-ui'

type Row = AdminTradingAccount & { client_id?: number; client_name?: string }

function loginMasked(r: Row): string {
  if (r.mt5_login_masked) return r.mt5_login_masked
  return maskLogin(r.mt5_login ?? r.mt5Login)
}

export function AdminTradingAccountsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [summaries, setSummaries] = useState<Record<number, Record<string, unknown>>>({})

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (fixtures) {
          const clientMap = Object.fromEntries(adminFixtureClients.map((c) => [c.id, c]))
          const flat = adminFixtureAccounts.map((a) => ({
            ...a,
            mt5_login_masked: a.mt5_login_masked || maskLogin(a.mt5_login),
            client_name: clientMap[a.client_id || 0]?.display_name,
          }))
          if (!cancelled) {
            setRows(flat)
            setSummaries({})
          }
          return
        }
        const clients = await adminApi.listClients({
          page: 1,
          pageSize: 50,
          hasTradingAccount: true,
          sort: 'id',
          order: 'desc',
        })
        const accountLists = await Promise.all(
          (clients.clients || []).slice(0, 25).map(async (c) => {
            try {
              const res = await adminApi.clientAccounts(c.id)
              return (res.accounts || []).map((a) => ({
                ...a,
                client_id: c.id,
                client_name: c.display_name || c.email,
              }))
            } catch {
              return [] as Row[]
            }
          }),
        )
        const flat = accountLists.flat()
        if (cancelled) return
        setRows(flat)

        const ids = flat.slice(0, 10).map((a) => a.id)
        const sumEntries = await Promise.all(
          ids.map(async (id) => {
            try {
              const s = await adminApi.accountSummary(id)
              return [id, s.account] as const
            } catch {
              return [id, {}] as const
            }
          }),
        )
        if (!cancelled) setSummaries(Object.fromEntries(sumEntries))
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load accounts'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fixtures])

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Trading accounts" title="MT5 account directory">
        <p className="cp-muted">Aggregated from client trading-account APIs.</p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Preview accounts only.
        </StateBlock>
      ) : (
        <StateBlock tone="info">
          There is no standalone admin trading-accounts index API. This view loads accounts from clients that have Demo
          accounts. Leverage / group / password fields are never shown.
        </StateBlock>
      )}

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading accounts…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rows={rows}
          rowKey={(r) => String(r.id)}
          empty={loading ? 'Loading accounts…' : 'No trading accounts found.'}
          columns={[
            {
              key: 'client',
              header: 'Client',
              render: (r) =>
                r.client_id ? (
                  <Link to={`/admin/clients/${r.client_id}${q}`}>
                    {r.client_name || `#${r.client_id}`}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              key: 'login',
              header: 'Masked login',
              render: (r) => loginMasked(r),
            },
            { key: 'server', header: 'Server', render: (r) => r.server || '—' },
            { key: 'mode', header: 'Mode', render: (r) => r.mode || '—' },
            {
              key: 'currency',
              header: 'Currency',
              render: (r) =>
                String(summaries[r.id]?.currency || r.currency || 'USD'),
            },
            { key: 'status', header: 'Status', render: (r) => r.status || '—' },
            {
              key: 'balance',
              header: 'Balance',
              render: (r) => money((summaries[r.id]?.balance as number | undefined) ?? r.balance),
            },
            {
              key: 'equity',
              header: 'Equity',
              render: (r) => money((summaries[r.id]?.equity as number | undefined) ?? r.equity),
            },
            {
              key: 'sync',
              header: 'Sync',
              render: (r) =>
                String(
                  summaries[r.id]?.sync_state ||
                    r.sync_state ||
                    (fixtures ? 'FIXTURE' : '—'),
                ),
            },
            {
              key: 'created',
              header: 'Created',
              render: (r) => formatWhen(r.created_at),
            },
            {
              key: 'open',
              header: 'Open',
              render: (r) => (
                <Link
                  className="pp-btn pp-btn--secondary pp-btn--sm"
                  to={`/admin/trading-accounts/${r.id}${q}`}
                >
                  Open
                </Link>
              ),
            },
          ]}
        />
      </div>
    </section>
  )
}
